import { ChallengeService } from '@duck/api-definitions'
import { CheckCircleIcon } from '@heroicons/react/24/outline'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
} from '@duck/ui'
import { useTar } from '@/modules'
import { useEffect, useState, type ComponentType } from 'react'
import { SubmitUserFeedback } from './SubmitUserFeedback'
import { FeedbackContent, SolutionContent } from './content'

type ViewMode = 'feedback' | 'solution' | 'submitFeedback'

interface FeedbackOverviewProps {
  isOpen: boolean
  onClose: () => void
  challenge: CompletedChallenge
  onShareFeedback?: (challenge: CompletedChallenge) => void
  onFeedbackSubmitted?: (feedback: ChallengeService.UserFeedback) => void
  initialViewMode?: ViewMode
  /** When true, show employer feedback and grade instead of AI feedback */
  showEmployer?: boolean
}

export interface CompletedChallenge extends ChallengeService.ChallengeInfo {
  icon?: ComponentType<{ className?: string }>
  estimatedTime?: string
}

export const FeedbackDialog = ({
  isOpen,
  onClose,
  challenge,
  onShareFeedback,
  onFeedbackSubmitted,
  initialViewMode = 'feedback',
  showEmployer = false
}: FeedbackOverviewProps) => {
  const { t } = useTar()
  const [viewMode, setViewMode] = useState<ViewMode>(initialViewMode)
  const [userFeedbackSubmitted, setUserFeedbackSubmitted] = useState<boolean>(!!challenge.challenge.userFeedback)

  useEffect(() => {
    if (isOpen) {
      setViewMode(initialViewMode)
    }
  }, [isOpen, initialViewMode])

  if (!challenge) {
    return null
  }

  const { task } = challenge

  const handleToggleView = () => {
    setViewMode((current) => (current === 'solution' ? 'feedback' : 'solution'))
  }

  const handleShareFeedbackClick = () => {
    onShareFeedback?.(challenge)
    setViewMode('submitFeedback')
  }

  const handleFeedbackSubmissionSuccess = (feedback: ChallengeService.UserFeedback) => {
    setUserFeedbackSubmitted(true)
    setViewMode('feedback')
    onFeedbackSubmitted?.(feedback)
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircleIcon className="w-8 h-8 text-success" />
              <div>
                <DialogTitle className="text-xl">
                  {viewMode === 'submitFeedback'
                    ? t('userFeedback.evaluateTheFeedback')
                    : t('challenges.resultsTitle')}
                </DialogTitle>
                <p className="text-sm text-muted-foreground">{task.name}</p>
              </div>
            </div>
            {viewMode === 'submitFeedback' && (
              <Button
                variant="outline"
                size="sm"
                className="me-4"
                onClick={() => setViewMode('feedback')}
              >
                {t('userFeedback.backToResults')}
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Content */}
        <div className="space-y-6 overflow-y-auto custom-scrollbar pe-2 max-h-[calc(90vh-200px)]">
          {viewMode === 'solution' ? (
            /* Solution View */
            <div className="h-full">
              <SolutionContent workState={challenge.challenge.workState} className="h-full" />
            </div>
          ) : viewMode === 'submitFeedback' ? (
            <SubmitUserFeedback
              key={challenge.challenge.id}
              challengeId={challenge.challenge.id}
              onCancel={() => setViewMode('feedback')}
              onSuccess={handleFeedbackSubmissionSuccess}
            />
          ) : (
            /* Original Feedback Content */
            <FeedbackContent challenge={challenge} showEmployer={showEmployer} />
          )}
        </div>

        {/* Footer */}
        {viewMode !== 'submitFeedback' && (
          <DialogFooter className="pt-4">
            <div className="flex items-center w-full">
              {!showEmployer && !userFeedbackSubmitted && (
                <Button variant="outline" onClick={handleShareFeedbackClick}>
                  {t('userFeedback.evaluateTheFeedback')}
                </Button>
              )}
              <Button
                variant="outline"
                className="ms-auto"
                onClick={handleToggleView}
              >
                {t(
                  viewMode === 'solution'
                    ? 'challenges.viewFeedback'
                    : 'challenges.viewSolution'
                )}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}
